
import React from 'react';

/**
 * Agrupa os dados recursivamente com base nas chaves de agrupamento.
 * @param {Array} rows - Lista de dados planos.
 * @param {Array} groupKeys - Lista de chaves (campos) para agrupar.
 * @param {Function} getValue - Função (item, key) => valor.
 * @returns {Array} - Lista de grupos ou linhas.
 */
export const groupData = (rows, groupKeys, getValue) => {
  if (!groupKeys || groupKeys.length === 0) return rows;

  const [currentKey, ...restKeys] = groupKeys;
  const groupsMap = new Map();

  rows.forEach((row) => {
    const rawValue = getValue(row, currentKey);
    const key = rawValue === null || rawValue === undefined ? 'N/D' : String(rawValue);
    
    if (!groupsMap.has(key)) {
      groupsMap.set(key, []);
    }
    groupsMap.get(key).push(row);
  });

  // Ordena os grupos alfabeticamente pela chave (pode ser customizado)
  const sortedKeys = Array.from(groupsMap.keys()).sort((a, b) => 
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );

  return sortedKeys.map((key) => {
    const groupRows = groupsMap.get(key);
    return {
      isGroup: true,
      groupKey: currentKey,
      groupValue: key,
      count: groupRows.length,
      // Se houver mais chaves, agrupa recursivamente; caso contrário, as "children" são as linhas
      children: restKeys.length > 0 
        ? groupData(groupRows, restKeys, getValue) 
        : groupRows,
      // Mantém referência às linhas originais deste grupo para agregações
      leafRows: groupRows 
    };
  });
};

/**
 * Calcula agregações para um conjunto de linhas.
 * @param {Array} rows - Linhas do grupo (leafRows).
 * @param {Object} config - Objeto { field: 'sum' | 'avg' | 'count' | 'min' | 'max' }.
 * @returns {Object} - Objeto com os resultados { field: valor }.
 */
export const aggregate = (rows, config) => {
  const result = {};
  if (!rows || rows.length === 0) return result;

  Object.keys(config).forEach((field) => {
    const type = config[field];
    const values = rows.map(r => {
        const val = r[field];
        // Tenta converter para número se for string numérica
        if (typeof val === 'string') {
            const clean = val.replace(/[^\d.,-]/g, '').replace(',', '.');
            const parsed = parseFloat(clean);
            return isNaN(parsed) ? 0 : parsed;
        }
        return typeof val === 'number' ? val : 0;
    });

    switch (type) {
      case 'sum':
        result[field] = values.reduce((a, b) => a + b, 0);
        break;
      case 'avg':
        result[field] = values.reduce((a, b) => a + b, 0) / values.length;
        break;
      case 'min':
        result[field] = Math.min(...values);
        break;
      case 'max':
        result[field] = Math.max(...values);
        break;
      case 'count':
        result[field] = rows.length;
        break;
      default:
        result[field] = null;
    }
  });

  return result;
};

/**
 * Formata um valor monetário para exibição nos subtotais.
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};
